import { FocusZone, FocusZoneDirection, List, mergeStyleSets, SearchBox, Spinner, SpinnerSize } from "@fluentui/react";
import { debounce, isNil } from "lodash";
import * as React from "react";
import { FunctionComponent, useEffect, useState } from "react";
import {
  Space,
  BookingsAndSpaceAvailabilities,
  useRoomBookingsAndAvailabilitiesOutlookLazyQuery,
  RoomBookingsAndAvailabilitiesOutlookQueryVariables,
  Floor,
} from "@graphql";
import { Mode } from "../../../AppRoomBookings";
import SpaceCard from "../space-card/SpaceCard";
import SpaceDetails from "../space-details/SpaceDetails";

export interface SpaceFilters {
  floorId: string;
  spaceType: string;
  amenities: string[];
}

interface Props {
  locationId: string;
  start: Date;
  end: Date;
  selectedSpaceId: string;
  filters: SpaceFilters;
  floors?: Floor[];
  outlookTimezone: string;
  onModeChange: (mode: Mode) => void;
  onAddSpaceToEvent: (space: Space) => void;
  onBackClick: () => void;
}

const Spaces: FunctionComponent<Props> = (props: Props) => {
  const {
    locationId,
    filters,
    start,
    end,
    selectedSpaceId,
    floors,
    outlookTimezone,
    onModeChange,
    onBackClick,
    onAddSpaceToEvent,
  } = props;
  const [spaceAvailabilities, setSpaceAvailabilities] = useState<BookingsAndSpaceAvailabilities[]>([]);
  const [filteredSpaceAvailabilities, setFilteredSpaceAvailabilities] = useState<BookingsAndSpaceAvailabilities[]>([]);
  const [selectedSpaceAvailability, setSelectedSpaceAvailability] = useState<BookingsAndSpaceAvailabilities>();
  const [search, setSearch] = useState<string>(null);
  const [availabilityVariables, setAvailabilityVariables] =
    useState<RoomBookingsAndAvailabilitiesOutlookQueryVariables>({
      locationId: locationId,
      floorId: filters.floorId,
      filters: filters.amenities
        ? {
            amenities: filters.amenities.map((name) => ({
              name,
            })),
          }
        : undefined,
      date: undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // browser
      // timezone: "Europe/London", // UTC
      // timezone: outlookTimezone, // outlook web client
      fromCalendarProvider: true,
      spaceTypes: [filters.spaceType ? filters.spaceType : "meetingroom"],
    });

  const [getData, { data: spacesData, loading }] = useRoomBookingsAndAvailabilitiesOutlookLazyQuery();

  useEffect(() => {
    // After props are initialized
    const newStartDateString = start?.toDateString();
    if (!newStartDateString) {
      // Fetch for today if no date was selected
      setAvailabilityVariables({ ...availabilityVariables, date: new Date().toDateString() });
    }
  }, []);

  useEffect(() => {
    const newStartDateString = start?.toDateString();
    if (newStartDateString && availabilityVariables.date != newStartDateString) {
      setAvailabilityVariables({ ...availabilityVariables, date: newStartDateString });
    }
  }, [start]);

  useEffect(() => {
    if (availabilityVariables && availabilityVariables.locationId && availabilityVariables.date) {
      getData({
        variables: availabilityVariables,
        pollInterval: 60000,
        fetchPolicy: "cache-and-network",
      });
    }
  }, [availabilityVariables]);

  useEffect(() => {
    setSpaceAvailabilities((spaceAvailabilities) => {
      if (!spaceAvailabilities) {
        return spaceAvailabilities;
      }
      spaceAvailabilities.map(
        (spaceAvailability) => (spaceAvailability.space.addedToOutlook = spaceAvailability.spaceId === selectedSpaceId),
      );
      return [...spaceAvailabilities];
    });
  }, [selectedSpaceId]);

  useEffect(() => {
    const filteredAvailabilities =
      spaceAvailabilities?.filter(
        (spaceAvailability) => !search || spaceAvailability.space.name.toLowerCase().includes(search.toLowerCase()),
      ) || [];

    setFilteredSpaceAvailabilities(filteredAvailabilities);

    if (selectedSpaceAvailability) {
      const newSelectedSpaceAvailability = spaceAvailabilities.find(
        (s) => s.spaceId === selectedSpaceAvailability.spaceId,
      );
      setSelectedSpaceAvailability(newSelectedSpaceAvailability);
    }
  }, [spaceAvailabilities, search]);

  // fetches spaces
  useEffect(() => {
    if (isNil(locationId)) {
      return undefined;
    }

    if (!isNil(spacesData) && spacesData.roomBookingsAndAvailabilities) {
      const spaceAvailabilitiesList = spacesData.roomBookingsAndAvailabilities;
      setSpaceAvailabilities(spaceAvailabilitiesList);
    }
  }, [spacesData, locationId]);

  const onSpaceClick = (id: string) => {
    setSelectedSpaceAvailability(spaceAvailabilities.find((s) => s.spaceId === id));
    onModeChange(Mode.SpaceDetails);
  };

  const onRenderCell = (item: BookingsAndSpaceAvailabilities, index: number | undefined) => {
    return (
      <SpaceCard
        index={index}
        spaceAvailability={item}
        floors={floors}
        onSpaceClick={onSpaceClick}
        onAddSpaceToEvent={onAddSpaceToEvent}
      />
    );
  };

  const onSearchTextChange = (search: string) => {
    setSearch(search);
  };

  const onDetailsBackClick = () => {
    setSelectedSpaceAvailability(null);
    onBackClick();
  };

  const debouncedChangeHandler = debounce(onSearchTextChange, 400);

  const classNames = mergeStyleSets({
    searchBar: {
      borderColor: "#D7D7D7",
      borderLeft: 0,
      borderRight: 0,
      color: "#615E5D",
      height: 50,
    },
  });

  return (
    <>
      {loading && <Spinner size={SpinnerSize.large}>Loading</Spinner>}
      {!loading &&
        filteredSpaceAvailabilities &&
        filteredSpaceAvailabilities.length > 0 &&
        !selectedSpaceAvailability && (
          <>
            <SearchBox
              className={classNames.searchBar}
              onChange={(e) => {
                if (e) return debouncedChangeHandler(e.target.value);
                else return debouncedChangeHandler("");
              }}
              iconProps={{ iconName: "Search", style: { color: "#AEAEAE" } }}
            />
            <FocusZone direction={FocusZoneDirection.vertical}>
              <List items={filteredSpaceAvailabilities} onRenderCell={onRenderCell} />
            </FocusZone>
          </>
        )}
      {!loading && filteredSpaceAvailabilities && filteredSpaceAvailabilities.length === 0 && (
        <div style={{ width: "100%", textAlign: "center", padding: 10 }}>No meeting rooms found.</div>
      )}
      {!loading && selectedSpaceAvailability && (
        <SpaceDetails
          start={start}
          end={end}
          spaceAvailability={selectedSpaceAvailability}
          onAddSpaceToEvent={onAddSpaceToEvent}
          onBackClick={onDetailsBackClick}
        />
      )}
    </>
  );
};

export default Spaces;
